import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Image, Home as HomeIcon, MapPin, Mail, Clock, Share2, UtensilsCrossed, Edit, X, Save } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import baremcLogo from "../assets/baremc-logo.png";
import GalleryCarousel3D from "../components/shared/GalleryCarousel3D";
import InteractiveCoin from "../components/3d/InteractiveCoin";
import { EditableImage, EditableBackground } from "../components/shared/EditableImage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [galleryAlbums, setGalleryAlbums] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [showLiveSection, setShowLiveSection] = useState(false);
  const [liveTimeRemaining, setLiveTimeRemaining] = useState("");
  const [liveSettings, setLiveSettings] = useState(null);
  const [otherCoins, setOtherCoins] = useState([]);
  const [activeCoinIdx, setActiveCoinIdx] = useState(0);
  const [coinFading, setCoinFading] = useState(false);
  const [foodCms, setFoodCms] = useState(null);
  const [tuesdayInfo, setTuesdayInfo] = useState(null);
  const [coinInfo, setCoinInfo] = useState(null);
  const [goldCoinCms, setGoldCoinCms] = useState(null);
  const [editingGoldCoin, setEditingGoldCoin] = useState(false);
  const [goldCoinForm, setGoldCoinForm] = useState({});

  // ─── Phase 2: Homepage layout / content ───
  const [layout, setLayout] = useState([]);
  const [pageContent, setPageContent] = useState({
    hero_tagline: "FÆLLESSKAB · FRIHED · LOYALITET",
    hero_logo_url: "https://customer-assets.emergentagent.com/job_riders-collective/artifacts/qos9b6ai_291A9EF4-2BE4-4271-BA5B-1C9215F4569F.png",
  });
  const [editingHero, setEditingHero] = useState(false);
  const [heroForm, setHeroForm] = useState({});
  const [uploadingHeroLogo, setUploadingHeroLogo] = useState(false);
  const [showLayoutPanel, setShowLayoutPanel] = useState(false);

  const { member, getToken, isModerator } = useAuth();

  useEffect(() => {
    // Fetch live section settings from backend
    const fetchLiveSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/live-section`);
        const settings = res.data;
        setLiveSettings(settings);
        
        if (settings.is_active && settings.activated_at) {
          const start = new Date(settings.activated_at);
          const hours = settings.duration_hours || 24;
          const endTime = new Date(start.getTime() + hours * 60 * 60 * 1000);
          const now = new Date();
          
          if (now < endTime) {
            setShowLiveSection(true);
            const diff = endTime - now;
            const h = Math.floor(diff / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            setLiveTimeRemaining(`${h}t ${m}m tilbage`);
          } else {
            setShowLiveSection(false);
          }
        } else {
          setShowLiveSection(false);
        }
      } catch {
        setShowLiveSection(false);
      }
    };
    
    fetchLiveSettings();
    const interval = setInterval(fetchLiveSettings, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        const now = new Date();
        const upcoming = res.data
          .filter(e => new Date(e.date) >= now)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 1); // Kun vis næste event
        setUpcomingEvents(upcoming);
      } catch (err) {
        console.error("Fejl ved hentning af events:", err);
      } finally {
        setLoadingEvents(false);
      }
    };

    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/albums`);
        // Get all albums for the 3D carousel
        setGalleryAlbums(res.data);
      } catch (err) {
        console.error("Fejl ved hentning af galleri:", err);
      } finally {
        setLoadingGallery(false);
      }
    };

    fetchEvents();
    fetchGallery();
  }, []);

  // Fetch food CMS data for "Dagens ret" section
  useEffect(() => {
    axios.get(`${API}/settings/bordbestilling`).then(r => setFoodCms(r.data)).catch(() => {});
    axios.get(`${API}/food-orders/next-tuesday`).then(r => setTuesdayInfo(r.data)).catch(() => {});
    axios.get(`${API}/coin-orders/info`).then(r => setCoinInfo(r.data)).catch(() => {});
    axios.get(`${API}/settings/gold-coin-section`).then(r => setGoldCoinCms(r.data)).catch(() => {});
    axios.get(`${API}/settings/homepage-layout`).then(r => setLayout(r.data.sections || [])).catch(() => {});
    axios.get(`${API}/settings/homepage-content`).then(r => setPageContent(r.data)).catch(() => {});
    axios.get(`${API}/settings/page/homepage`).then(r => setImageOverrides(r.data.fields || {})).catch(() => {});
  }, []);

  const reloadImageOverrides = () => {
    axios.get(`${API}/settings/page/homepage`).then(r => setImageOverrides(r.data.fields || {})).catch(() => {});
  };
  const imgOverride = (slot) => imageOverrides[`image_${slot}`];

  // Fetch "The Others mønter" for slideshow
  useEffect(() => {
    const fetchCoins = async () => {
      try {
        const res = await axios.get(`${API}/coins`);
        const filtered = res.data.filter(c => c.category === "The Others mønter" && c.image_url);
        setOtherCoins(filtered);
      } catch {}
    };
    fetchCoins();
  }, []);

  const saveGoldCoinCms = async () => {
    try {
      const token = getToken();
      const res = await axios.put(`${API}/settings/gold-coin-section`, goldCoinForm, { headers: { Authorization: `Bearer ${token}` } });
      setGoldCoinCms(res.data);
      setEditingGoldCoin(false);
      toast.success("Guldmønt-sektion opdateret!");
    } catch { toast.error("Kunne ikke gemme"); }
  };

  const [uploadingPromo, setUploadingPromo] = useState(false);
  const [imageOverrides, setImageOverrides] = useState({});
  const handlePromoImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPromo(true);
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/settings/gold-coin-section/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const fullUrl = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      setGoldCoinForm((p) => ({ ...p, image_url: fullUrl }));
      toast.success("Billede uploadet!");
    } catch {
      toast.error("Kunne ikke uploade billede");
    } finally {
      setUploadingPromo(false);
      e.target.value = "";
    }
  };

  // ─── Phase 2 helpers: Layout & Hero CMS ───
  const sectionMap = layout.reduce((acc, s) => { acc[s.key] = s; return acc; }, {});
  const sectionOrder = (key, fallback) => sectionMap[key]?.order ?? fallback;
  const sectionVisible = (key) => sectionMap[key]?.visible !== false;

  const saveLayout = async (newSections) => {
    try {
      const token = getToken();
      const res = await axios.put(`${API}/settings/homepage-layout`, { sections: newSections }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLayout(res.data.sections);
    } catch { toast.error("Kunne ikke gemme layout"); }
  };

  const moveSection = (key, direction) => {
    const sorted = [...layout].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(s => s.key === key);
    if (idx < 0) return;
    const swap = idx + direction;
    if (swap < 0 || swap >= sorted.length) return;
    [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
    const reordered = sorted.map((s, i) => ({ ...s, order: i }));
    setLayout(reordered);
    saveLayout(reordered);
  };

  const toggleSectionVisibility = (key) => {
    const updated = layout.map(s => s.key === key ? { ...s, visible: !s.visible } : s);
    setLayout(updated);
    saveLayout(updated);
  };

  const saveHeroContent = async () => {
    try {
      const token = getToken();
      const res = await axios.put(`${API}/settings/homepage-content`, heroForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPageContent(res.data);
      setEditingHero(false);
      toast.success("HERO opdateret!");
    } catch { toast.error("Kunne ikke gemme"); }
  };

  const handleHeroLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHeroLogo(true);
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/settings/homepage-content/upload-logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const fullUrl = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      setHeroForm(p => ({ ...p, hero_logo_url: fullUrl }));
      toast.success("Logo uploadet!");
    } catch {
      toast.error("Kunne ikke uploade logo");
    } finally {
      setUploadingHeroLogo(false);
      e.target.value = "";
    }
  };

  // Admin control overlay rendered absolutely on each section
  const SectionControls = ({ sectionKey }) => {
    if (!isModerator) return null;
    const s = sectionMap[sectionKey];
    return (
      <div
        className="absolute top-2 right-2 z-30 flex items-center gap-1 bg-black/85 backdrop-blur-sm border border-purple-500/40 rounded-lg p-1 shadow-lg"
        data-testid={`section-controls-${sectionKey}`}
      >
        <button
          onClick={() => moveSection(sectionKey, -1)}
          className="p-1.5 rounded text-white hover:bg-purple-600/30 transition-colors"
          title="Flyt op"
          data-testid={`section-up-${sectionKey}`}
        >↑</button>
        <button
          onClick={() => moveSection(sectionKey, +1)}
          className="p-1.5 rounded text-white hover:bg-purple-600/30 transition-colors"
          title="Flyt ned"
          data-testid={`section-down-${sectionKey}`}
        >↓</button>
        <button
          onClick={() => toggleSectionVisibility(sectionKey)}
          className={`p-1.5 rounded transition-colors ${s?.visible === false ? "text-red-400 bg-red-500/20" : "text-white hover:bg-purple-600/30"}`}
          title={s?.visible === false ? "Skjult — klik for at vise" : "Skjul sektion"}
          data-testid={`section-hide-${sectionKey}`}
        >{s?.visible === false ? "👁‍🗨" : "👁"}</button>
      </div>
    );
  };


  // Auto-rotate coin slideshow
  useEffect(() => {
    if (otherCoins.length < 2) return;
    const timer = setInterval(() => {
      setCoinFading(true);
      setTimeout(() => {
        setActiveCoinIdx(prev => (prev + 1) % otherCoins.length);
        setCoinFading(false);
      }, 600);
    }, 2600);
    return () => clearInterval(timer);
  }, [otherCoins]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const scrollToSection = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  const socialLinks = [
    { name: 'Facebook', url: 'https://www.facebook.com/share/g/1HAhbwvoBX/?mibextid=wwXIfr', icon: 'fb', color: '#1877F2' },
    { name: 'TikTok', url: 'https://www.tiktok.com/@theothers.dk?_r=1&_t=ZN-943TAWcvtm2', icon: 'tiktok', color: '#000000' },
    { name: 'YouTube', url: 'https://www.youtube.com/watch?v=9stUIbnKJFo', icon: 'yt', color: '#FF0000' },
    { name: 'Spotify', url: 'https://open.spotify.com/artist/6qouo4AZG13z92FE3e8Abn?si=pjqNTKPgSHupRMTvpr3T3w', icon: 'spotify', color: '#1DB954' },
    { name: 'Instagram', url: 'https://www.instagram.com/the_others_mc?igsh=MWR0ZXk2NTlyeWZ4bA==', icon: 'instagram', color: '#E4405F' },
  ];

  const handleShare = async () => {
    const shareData = {
      title: 'The Others MC Danmark',
      text: 'Bliv en del af fællesskabet - The Others Motorcykelklub',
      url: window.location.origin,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.origin);
        toast.success('Link kopieret til udklipsholder');
      } catch {
        toast.error('Kunne ikke kopiere link');
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="home-page">
      {/* Phase 2: Floating layout-builder button for admins */}
      {isModerator && (
        <button
          onClick={() => setShowLayoutPanel(p => !p)}
          className="fixed bottom-5 right-5 z-50 bg-purple-600 hover:bg-purple-500 text-white px-4 py-2.5 rounded-full shadow-2xl flex items-center gap-2 text-sm font-bold font-['Oswald'] uppercase tracking-wider"
          style={{ boxShadow: "0 0 20px rgba(168, 85, 247, 0.6)" }}
          data-testid="layout-builder-toggle"
        >
          {showLayoutPanel ? "Luk" : "✏ Layout"}
        </button>
      )}

      {/* Phase 2: Layout-builder side panel */}
      {isModerator && showLayoutPanel && (
        <div
          className="fixed inset-y-0 right-0 z-40 w-80 max-w-[90vw] bg-neutral-950/98 border-l border-purple-500/30 shadow-2xl overflow-y-auto p-4"
          data-testid="layout-builder-panel"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-['Oswald'] font-bold uppercase tracking-wider text-sm">Forsidens sektioner</h3>
            <button onClick={() => setShowLayoutPanel(false)} className="text-gray-400 hover:text-white text-2xl">×</button>
          </div>
          <p className="text-gray-400 text-xs mb-4">Træk sektioner op/ned eller skjul dem. Ændringer gemmes automatisk.</p>
          <ul className="space-y-2">
            {[...layout].sort((a, b) => a.order - b.order).map((s, idx) => (
              <li key={s.key} className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg p-2" data-testid={`layout-row-${s.key}`}>
                <span className="text-gray-500 text-xs w-6">{idx + 1}</span>
                <span className={`flex-1 text-sm ${s.visible === false ? "text-gray-500 line-through" : "text-white"}`}>{s.label || s.key}</span>
                <button onClick={() => moveSection(s.key, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-white/10 disabled:opacity-30" data-testid={`layout-up-${s.key}`}>↑</button>
                <button onClick={() => moveSection(s.key, +1)} disabled={idx === layout.length - 1} className="p-1 rounded hover:bg-white/10 disabled:opacity-30" data-testid={`layout-down-${s.key}`}>↓</button>
                <button
                  onClick={() => toggleSectionVisibility(s.key)}
                  className={`p-1 rounded ${s.visible === false ? "text-red-400" : "text-emerald-400 hover:bg-white/10"}`}
                  data-testid={`layout-vis-${s.key}`}
                >{s.visible === false ? "Skjult" : "Synlig"}</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* ===== HERO SECTION ===== */}
      {sectionVisible("hero") && (
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ order: sectionOrder("hero", 0) }}
        data-testid="section-hero"
      >
        <SectionControls sectionKey="hero" />
        <EditableBackground
          pageKey="homepage"
          slot="hero-bg"
          defaultSrc="https://customer-assets.emergentagent.com/job_5d68edee-09f7-4dc1-a893-b23881a611f6/artifacts/6r4lnxbz_48f1c9f5-9bfa-4b23-96ca-b4459ef0af69.jpeg"
          overrideSrc={imgOverride("hero-bg")}
          onSaved={reloadImageOverrides}
          className="absolute inset-0"
        />
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, 
              rgba(0, 0, 0, 0.3) 0%, 
              rgba(0, 0, 0, 0.2) 30%, 
              rgba(0, 0, 0, 0.4) 60%, 
              rgba(5, 5, 8, 0.7) 80%, 
              rgba(5, 5, 8, 0.9) 95%, 
              rgb(5, 5, 8) 100%)`
          }}
        />
        
        <div className="relative z-10 text-center px-4 pt-20 pb-12 flex flex-col items-center">
          {isModerator && !editingHero && (
            <button
              onClick={() => { setHeroForm({ ...pageContent }); setEditingHero(true); }}
              className="absolute -top-2 left-2 bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm z-20"
              data-testid="edit-hero-btn"
            >
              <Edit size={14} /> Rediger Hero
            </button>
          )}

          {editingHero && (
            <div className="mb-6 w-full max-w-md p-5 rounded-xl bg-black/90 border border-blue-500/30 text-left z-30">
              <h3 className="text-base font-bold text-white mb-3">Rediger Hero</h3>
              <label className="block text-xs text-gray-400 mb-1">Logo</label>
              {heroForm.hero_logo_url && (
                <img src={heroForm.hero_logo_url} alt="preview" className="max-h-28 mb-2 rounded-lg border border-white/10" />
              )}
              <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs cursor-pointer mb-3" data-testid="hero-logo-upload-btn">
                {uploadingHeroLogo ? "Uploader..." : "Upload nyt logo"}
                <input type="file" accept="image/*" onChange={handleHeroLogoUpload} disabled={uploadingHeroLogo} className="hidden" />
              </label>
              <input
                value={heroForm.hero_logo_url || ""}
                onChange={e => setHeroForm(p => ({ ...p, hero_logo_url: e.target.value }))}
                placeholder="…eller indsæt URL"
                className="mc-input w-full text-xs mb-3"
              />
              <label className="block text-xs text-gray-400 mb-1">Slogan (under logoet)</label>
              <input
                value={heroForm.hero_tagline || ""}
                onChange={e => setHeroForm(p => ({ ...p, hero_tagline: e.target.value }))}
                className="mc-input w-full text-sm mb-3"
                data-testid="hero-tagline-input"
              />
              <div className="flex gap-2">
                <button onClick={saveHeroContent} className="chrome-btn px-3 py-1.5 rounded text-sm flex items-center gap-1.5" data-testid="hero-save-btn"><Save size={14} /> Gem</button>
                <button onClick={() => setEditingHero(false)} className="ghost-blue-btn px-3 py-1.5 rounded text-sm flex items-center gap-1.5"><X size={14} /> Annuller</button>
              </div>
            </div>
          )}

          <div className="mb-6 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-72 h-72 sm:w-96 sm:h-96 lg:w-[500px] lg:h-[500px] rounded-full bg-blue-500/30 blur-3xl animate-pulse"></div>
              <div className="absolute w-56 h-56 sm:w-72 sm:h-72 lg:w-96 lg:h-96 rounded-full bg-blue-400/20 blur-2xl"></div>
            </div>
            <img
              src={pageContent.hero_logo_url}
              alt="The Others MC Danmark"
              className="w-72 h-72 sm:w-96 sm:h-96 lg:w-[450px] lg:h-[450px] object-contain relative z-10"
              style={{ filter: 'drop-shadow(0 0 30px rgba(37, 99, 235, 0.4)) drop-shadow(0 0 60px rgba(37, 99, 235, 0.2))' }}
              data-testid="club-logo"
            />
          </div>
          
          <p className="text-base sm:text-xl lg:text-2xl text-white mx-auto mb-10 font-['Oswald'] tracking-[0.2em] sm:tracking-[0.3em] uppercase font-bold px-4"
             style={{ textShadow: '2px 2px 10px rgba(0,0,0,0.9), 0 0 30px rgba(37, 99, 235, 0.4), 0 0 60px rgba(37, 99, 235, 0.2)' }}
             data-testid="hero-tagline">
            {pageContent.hero_tagline}
          </p>
          
          <div className="mt-8 flex flex-col items-center gap-3 animate-bounce cursor-pointer" onClick={scrollToSection}>
            <p className="text-gray-400 text-sm uppercase tracking-widest font-['Oswald']">Udforsk The Others</p>
            <div className="w-10 h-16 border-2 border-gray-500 rounded-full flex items-start justify-center p-2">
              <div className="w-2 h-3 bg-gray-400 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ===== PROMO SECTION (CMS) ===== */}
      {sectionVisible("promo") && (
      <section className="relative py-8 sm:py-12 px-4" data-testid="coin-section" style={{ order: sectionOrder("promo", 1) }}>
        <SectionControls sectionKey="promo" />
        <div className="max-w-4xl mx-auto text-center relative">

          {/* Admin/editor edit button */}
          {isModerator && !editingGoldCoin && (
            <button
              onClick={() => { setGoldCoinForm({ ...goldCoinCms }); setEditingGoldCoin(true); }}
              className="absolute -top-2 right-0 bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm z-20"
              data-testid="edit-gold-coin-btn"
            >
              <Edit size={16} /> Rediger
            </button>
          )}

          {/* Admin edit form */}
          {editingGoldCoin && (
            <div className="mb-8 p-6 rounded-xl bg-black/80 border border-white/10 text-left">
              <h3 className="text-lg font-bold text-white mb-4">Rediger sponsor-sektion</h3>

              {/* Image upload + preview */}
              <div className="mb-5">
                <label className="block text-sm text-gray-400 mb-2">Billede</label>
                {goldCoinForm.image_url && (
                  <div className="mb-3">
                    <img src={goldCoinForm.image_url} alt="preview" className="max-h-48 rounded-lg border border-white/10" />
                  </div>
                )}
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg cursor-pointer text-sm font-medium" data-testid="promo-upload-btn">
                  {uploadingPromo ? "Uploader..." : "Upload nyt billede"}
                  <input type="file" accept="image/*" onChange={handlePromoImageUpload} disabled={uploadingPromo} className="hidden" />
                </label>
                <p className="text-xs text-gray-500 mt-2">Eller indsæt en URL manuelt:</p>
                <input
                  value={goldCoinForm.image_url || ""}
                  onChange={e => setGoldCoinForm({ ...goldCoinForm, image_url: e.target.value })}
                  className="mc-input w-full mt-1"
                  placeholder="https://..."
                  data-testid="promo-image-url-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Knaptekst</label>
                  <input value={goldCoinForm.button_text || ""} onChange={e => setGoldCoinForm({ ...goldCoinForm, button_text: e.target.value })} className="mc-input w-full" data-testid="gold-coin-btn-text-input" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Knap-link (URL)</label>
                  <input value={goldCoinForm.button_link || ""} onChange={e => setGoldCoinForm({ ...goldCoinForm, button_link: e.target.value })} className="mc-input w-full" placeholder="https://..." data-testid="gold-coin-btn-link-input" />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={saveGoldCoinCms} className="chrome-btn px-4 py-2 rounded-lg flex items-center gap-2" data-testid="gold-coin-save-btn"><Save size={16} /> Gem</button>
                <button onClick={() => setEditingGoldCoin(false)} className="ghost-blue-btn px-4 py-2 rounded-lg flex items-center gap-2"><X size={16} /> Annuller</button>
              </div>
            </div>
          )}

          {/* Main image */}
          {goldCoinCms?.image_url && (
            <img
              src={goldCoinCms.image_url}
              alt=""
              className="w-full max-w-3xl mx-auto rounded-xl shadow-2xl"
              data-testid="promo-section-image"
            />
          )}

          {/* Link button */}
          {goldCoinCms?.button_link && (
            <div className="mt-6">
              <a
                href={goldCoinCms.button_link}
                target="_blank"
                rel="noopener noreferrer"
                className="chrome-btn px-8 py-3 inline-flex items-center gap-2 text-sm sm:text-base font-bold"
                data-testid="auction-event-link"
              >
                {goldCoinCms?.button_text || "Se mere"}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
            </div>
          )}
        </div>
      </section>
      )}

      {/* ===== COIN VALUE SECTION ===== */}
      {sectionVisible("coin_value") && (
      <section className="py-12 px-4 text-center relative" data-testid="section-coin-value" style={{ order: sectionOrder("coin_value", 2) }}>
        <SectionControls sectionKey="coin_value" />
        {/* Coin image slideshow */}
        {otherCoins.length > 0 && (
          <div className="mb-8">
            <p className="text-gray-500 text-xs uppercase tracking-widest font-['Oswald'] mb-4">The Others Mønter</p>
            <div className="relative w-40 h-40 sm:w-48 sm:h-48 mx-auto">
              <div className="absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.1) 0%, transparent 70%)" }}></div>
              {otherCoins.map((coin, i) => (
                <div
                  key={coin.coin_id || i}
                  className="absolute inset-0 flex items-center justify-center transition-all duration-600"
                  style={{
                    opacity: i === activeCoinIdx ? (coinFading ? 0 : 1) : 0,
                    transform: i === activeCoinIdx
                      ? (coinFading ? "scale(0.7) rotate(90deg)" : "scale(1) rotate(0deg)")
                      : "scale(0.7) rotate(-90deg)",
                    transition: "opacity 0.6s ease, transform 0.6s ease",
                  }}
                >
                  <img
                    src={coin.image_url?.startsWith("http") ? coin.image_url : `${BACKEND_URL}${coin.image_url}`}
                    alt={coin.name}
                    className="w-36 h-36 sm:w-44 sm:h-44 rounded-full object-cover"
                    style={{ boxShadow: "0 0 30px rgba(212,175,55,0.2)" }}
                  />
                </div>
              ))}
              <div className="absolute -bottom-8 left-0 right-0 text-center">
                <p className="text-xs font-['Oswald'] uppercase tracking-wider transition-opacity duration-300" style={{ color: "#d4af37", opacity: coinFading ? 0 : 1 }}>
                  {otherCoins[activeCoinIdx]?.name}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="mt-12">
          <Link
            to="/monter"
            className="chrome-btn px-6 py-3 inline-flex items-center gap-2 text-sm"
            data-testid="check-coin-value-btn"
          >
            Tjek værdi af din mønt
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>
      )}

      {/* ===== THE OTHERS MUSIK SECTION ===== */}
      {sectionVisible("musik") && (
      <section 
        className="relative py-20 px-4 overflow-hidden cursor-pointer group"
        onClick={() => window.open('https://open.spotify.com/artist/6qouo4AZG13z92FE3e8Abn?si=pjqNTKPgSHupRMTvpr3T3w', '_blank')}
        data-testid="music-section"
        style={{ order: sectionOrder("musik", 3) }}
      >
        <SectionControls sectionKey="musik" />
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              linear-gradient(to bottom, 
                rgba(5,5,5,1) 0%, 
                rgba(5,5,8,0) 15%, 
                rgba(10,13,20,0.95) 50%, 
                rgba(5,5,8,0) 85%, 
                rgba(5,5,5,1) 100%
              )
            `
          }}
        />
        
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(10,15,25,0.98) 0%, rgba(5,5,8,1) 70%)'
          }}
        />

        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute top-1/2 left-1/3 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-25 blur-3xl animate-pulse"
            style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.5) 0%, transparent 60%)' }}
          />
          <div 
            className="absolute top-1/2 right-1/3 -translate-y-1/2 w-80 h-80 rounded-full opacity-20 blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(148,163,184,0.4) 0%, transparent 60%)' }}
          />
        </div>

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute left-[8%] bottom-0 text-[#00d4ff] text-4xl font-bold float-note-1" style={{ textShadow: '0 0 20px rgba(0,212,255,0.8)' }}>♪</div>
          <div className="absolute left-[15%] bottom-0 text-[#00d4ff]/80 text-5xl font-bold float-note-2" style={{ textShadow: '0 0 25px rgba(0,212,255,0.6)' }}>♫</div>
          <div className="absolute left-[5%] bottom-0 text-slate-400 text-3xl font-bold float-note-3" style={{ textShadow: '0 0 15px rgba(148,163,184,0.7)' }}>♪</div>
          <div className="absolute left-[20%] bottom-0 text-[#00d4ff] text-4xl font-bold float-note-4" style={{ textShadow: '0 0 20px rgba(0,212,255,0.9)' }}>♬</div>
          <div className="absolute left-[12%] bottom-0 text-[#00d4ff]/70 text-6xl font-bold float-note-5" style={{ textShadow: '0 0 30px rgba(0,212,255,0.5)' }}>♫</div>
          
          <div className="absolute right-[8%] bottom-0 text-[#00d4ff] text-4xl font-bold float-note-6" style={{ textShadow: '0 0 20px rgba(0,212,255,0.8)' }}>♪</div>
          <div className="absolute right-[15%] bottom-0 text-slate-300 text-5xl font-bold float-note-7" style={{ textShadow: '0 0 25px rgba(203,213,225,0.7)' }}>♫</div>
          <div className="absolute right-[5%] bottom-0 text-[#00d4ff]/80 text-3xl font-bold float-note-8" style={{ textShadow: '0 0 15px rgba(0,212,255,0.6)' }}>♬</div>
          
          <div className="absolute left-[35%] bottom-0 text-[#00d4ff]/60 text-3xl float-note-3" style={{ textShadow: '0 0 15px rgba(0,212,255,0.4)', animationDelay: '0.5s' }}>♪</div>
          <div className="absolute right-[35%] bottom-0 text-[#00d4ff]/50 text-4xl float-note-5" style={{ textShadow: '0 0 18px rgba(0,212,255,0.3)', animationDelay: '1.2s' }}>♫</div>
        </div>

        <div 
          className="absolute top-0 left-0 right-0 h-16 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(5,5,8,1) 0%, transparent 100%)' }}
        />
        
        <div 
          className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(5,5,8,1) 0%, transparent 100%)' }}
        />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div 
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
              style={{
                background: 'linear-gradient(135deg, #1DB954 0%, #1ed760 100%)',
                boxShadow: '0 4px 20px rgba(29, 185, 84, 0.4)'
              }}
            >
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
            </div>
          </div>
          
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-['Oswald'] font-bold text-white tracking-wide mb-3 group-hover:text-[#00d4ff] transition-colors"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}
          >
            THE OTHERS MUSIK
          </h2>
          <div className="industrial-divider w-20 mx-auto mb-4"></div>
          <p className="text-gray-500 text-base sm:text-lg mb-6 group-hover:text-gray-400 transition-colors">
            Nyd vores musik på turen
          </p>
          
          <div 
            className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full transition-all duration-300 group-hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #1DB954 0%, #1ed760 100%)',
              boxShadow: '0 4px 20px rgba(29, 185, 84, 0.3)'
            }}
          >
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <span className="text-white font-['Oswald'] uppercase tracking-wider text-sm font-medium">
              Hør Musikken
            </span>
          </div>
        </div>
      </section>
      )}

      {/* ===== KOMMENDE EVENTS SECTION ===== */}
      {sectionVisible("events") && (
      <section className="py-12 px-4 relative" data-testid="upcoming-events-section" style={{ order: sectionOrder("events", 4) }}>
        <SectionControls sectionKey="events" />
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white tracking-wide mb-2 neon-text">
              NÆSTE EVENT
            </h2>
            <div className="industrial-divider w-16 mx-auto"></div>
          </div>

          {loadingEvents ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border border-[#00d4ff]/50 border-t-[#00d4ff] rounded-full animate-spin"></div>
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="max-w-lg mx-auto">
              {upcomingEvents.map((event, index) => (
                <Link 
                  key={event.event_id} 
                  to="/kalender"
                  className="group block"
                  data-testid={`event-card-${index}`}
                >
                  <div className="mc-card relative overflow-hidden transition-all duration-500 group-hover:-translate-y-2" style={{ boxShadow: '0 0 30px rgba(0,0,0,0.8)' }}>
                    {event.image ? (
                      <div className="h-44 overflow-hidden">
                        <img 
                          src={event.image.startsWith('http') ? event.image : `${BACKEND_URL}${event.image}`}
                          alt={event.title}
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#050508] via-transparent to-transparent"></div>
                      </div>
                    ) : (
                      <div className="h-32 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.05) 0%, transparent 100%)' }}>
                        <CalendarIcon size={32} className="text-gray-700" />
                      </div>
                    )}
                    <div className="p-5">
                      <p className="text-[#00d4ff] text-xs font-['Oswald'] uppercase tracking-widest mb-2" style={{ textShadow: '0 0 10px rgba(0,212,255,0.5)' }}>
                        {formatDate(event.date)}
                      </p>
                      <h3 className="text-white font-['Oswald'] text-lg font-medium group-hover:text-[#00d4ff] transition-colors">
                        {event.title}
                      </h3>
                      {event.location && (
                        <p className="text-gray-600 text-xs mt-2 flex items-center gap-1.5">
                          <MapPin size={12} className="text-[#00d4ff]/50" /> {event.location}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarIcon size={28} className="mx-auto text-gray-700 mb-3" />
              <p className="text-gray-500 text-sm">Ingen kommende events lige nu</p>
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/kalender" className="chrome-btn px-6 py-3 inline-flex items-center gap-2 text-sm">
              Se Alle Events <CalendarIcon size={16} />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* ===== LIVE FRA MESSEN SECTION (Synlig i 24 timer) ===== */}
      {sectionVisible("live") && showLiveSection && (
      <section className="py-12 px-4 relative overflow-hidden" data-testid="messe-section" style={{ order: sectionOrder("live", 5) }}>
        <SectionControls sectionKey="live" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(37,99,235,0.2) 0%, transparent 50%)' }}></div>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 70% 50%, rgba(148,163,184,0.15) 0%, transparent 50%)' }}></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
                 style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.2) 0%, rgba(239,68,68,0.1) 100%)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-red-400 text-xs font-['Oswald'] uppercase tracking-widest">Live</span>
              {liveTimeRemaining && (
                <span className="text-red-400/60 text-xs ml-2">• {liveTimeRemaining}</span>
              )}
            </div>
            <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white tracking-wide mb-2">
              {liveSettings?.title || "LIVE FRA MESSEN"}
            </h2>
            <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Billede 1 - Stort */}
            <div className="md:col-span-2 group relative overflow-hidden rounded-xl aspect-[16/10]"
                 style={{ boxShadow: '0 15px 40px -10px rgba(0,0,0,0.5)' }}>
              <EditableImage
                pageKey="homepage"
                slot="messe-1"
                defaultSrc="https://customer-assets.emergentagent.com/job_f6bcc1dc-d12a-4128-81af-7c0c65f190dc/artifacts/o0d9dhvh_IMG_4082.jpeg"
                overrideSrc={imgOverride("messe-1")}
                onSaved={reloadImageOverrides}
                alt="The Others MC på messen"
                className="w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-white font-['Oswald'] text-base tracking-wide">Klubbens stand</p>
              </div>
            </div>

            {/* Billede 2 */}
            <div className="group relative overflow-hidden rounded-xl aspect-[4/5] md:aspect-auto"
                 style={{ boxShadow: '0 15px 40px -10px rgba(0,0,0,0.5)' }}>
              <EditableImage
                pageKey="homepage"
                slot="messe-2"
                defaultSrc="https://customer-assets.emergentagent.com/job_f6bcc1dc-d12a-4128-81af-7c0c65f190dc/artifacts/s7hm2fvb_IMG_4083.jpeg"
                overrideSrc={imgOverride("messe-2")}
                onSaved={reloadImageOverrides}
                alt="Medlem på motorcykel"
                className="w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-white font-['Oswald'] text-base tracking-wide">Style & Power</p>
              </div>
            </div>

            {/* Billede 3 */}
            <div className="md:col-span-3 group relative overflow-hidden rounded-xl aspect-[21/9]"
                 style={{ boxShadow: '0 15px 40px -10px rgba(0,0,0,0.5)' }}>
              <EditableImage
                pageKey="homepage"
                slot="messe-3"
                defaultSrc="https://customer-assets.emergentagent.com/job_f6bcc1dc-d12a-4128-81af-7c0c65f190dc/artifacts/18i9asz1_IMG_4084.jpeg"
                overrideSrc={imgOverride("messe-3")}
                onSaved={reloadImageOverrides}
                alt="Motorcykel på messen"
                className="w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-white font-['Oswald'] text-lg tracking-wide">Frihed på to hjul</p>
                <p className="text-gray-400 text-sm mt-1">Kom forbi vores stand og mød klubben</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ===== KLUBBEN SECTION ===== */}
      {sectionVisible("klubben") && (
      <section className="py-12 px-4 relative" data-testid="klubben-section" style={{ order: sectionOrder("klubben", 6) }}>
        <SectionControls sectionKey="klubben" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(37,99,235,0.15) 0%, transparent 60%)' }}></div>
        </div>
        
        <div className="max-w-3xl mx-auto relative z-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white tracking-wide mb-2 neon-text">
            KLUBBEN
          </h2>
          <div className="industrial-divider w-16 mx-auto mb-4"></div>
          <p className="text-gray-400 leading-relaxed mb-4 max-w-lg mx-auto text-sm">
            Besøg vores klubhus og oplev det ægte fællesskab.
          </p>
          
          <div className="max-w-xl mx-auto mb-6">
            <div className="aspect-[16/9] rounded-xl overflow-hidden"
                 style={{ boxShadow: '0 15px 40px -10px rgba(0,0,0,0.5)' }}>
              <EditableImage
                pageKey="homepage"
                slot="klubben"
                defaultSrc="https://customer-assets.emergentagent.com/job_1bd601c1-568a-4b0a-a3a1-09f101a23af6/artifacts/5vwsq3rp_IMG_4066.jpeg"
                overrideSrc={imgOverride("klubben")}
                onSaved={reloadImageOverrides}
                alt="Klubben"
                className="w-full h-full"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm">
            <div className="flex items-center gap-2 text-gray-500">
              <Clock size={14} className="text-blue-500/70" />
              <span>Tirs & Fre fra 18:00</span>
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <MapPin size={14} className="text-blue-500/70" />
              <span>Marienbergvej 92, Vordingborg</span>
            </div>
          </div>
          
          <Link to="/klubben" className="chrome-btn px-6 py-3 inline-flex items-center gap-2 text-sm">
            Besøg Klubben <HomeIcon size={16} />
          </Link>
        </div>
      </section>
      )}

      {/* ===== DAGENS RET & BORDBESTILLING SECTION ===== */}
      {sectionVisible("food") && (
      <section className="py-12 px-4 relative" data-testid="food-section" style={{ order: sectionOrder("food", 7) }}>
        <SectionControls sectionKey="food" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(212,175,55,0.15) 0%, transparent 60%)' }}></div>
        </div>
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white tracking-wide mb-2 neon-text">
              TIRSDAGENS AFTENSMAD
            </h2>
            <div className="industrial-divider w-16 mx-auto mb-3"></div>
            {tuesdayInfo && (
              <p className="text-gray-500 text-sm">Næste tirsdag: {tuesdayInfo.event_date_formatted}</p>
            )}
          </div>

          {/* Dagens ret card */}
          <div className="bg-gradient-to-br from-amber-900/20 to-amber-800/5 border border-amber-500/30 rounded-2xl overflow-hidden mb-4"
            style={{ boxShadow: "0 0 30px rgba(212,175,55,0.08)" }} data-testid="dagens-ret-card">
            {foodCms?.hero_image && (
              <div className="h-48 sm:h-56 overflow-hidden">
                <img src={foodCms.hero_image} alt="Dagens ret" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-amber-400/70 text-xs uppercase tracking-wider font-['Oswald'] mb-1">Dagens ret</p>
                  <h3 className="text-xl sm:text-2xl font-['Oswald'] font-bold text-white" data-testid="dagens-ret-title">
                    {foodCms?.dagens_ret || "Annonceres snart"}
                  </h3>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <span className="text-2xl font-['Oswald'] font-black text-amber-400">{tuesdayInfo?.price || 60}</span>
                  <span className="text-amber-400/70 text-sm ml-1">kr</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                {foodCms?.info_text || "Hver tirsdag serverer vi varm aftensmad i klubben."}
              </p>
              {tuesdayInfo && tuesdayInfo.orders_count > 0 && (
                <p className="text-emerald-400/80 text-xs mb-4" data-testid="home-orders-count">
                  {tuesdayInfo.orders_count} har allerede bestilt
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/bordbestilling" className="chrome-btn px-6 py-3 inline-flex items-center justify-center gap-2 text-sm flex-1" data-testid="food-section-btn">
                  Bestil og betal <UtensilsCrossed size={16} />
                </Link>
                <Link to="/bordbestilling" className="px-6 py-3 inline-flex items-center justify-center gap-2 text-sm text-amber-400/80 border border-amber-500/20 rounded-lg hover:bg-amber-500/10 transition-all" data-testid="alacarte-section-btn">
                  Se à la carte menu
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ===== COIN RESERVATION BANNER ===== */}
      {sectionVisible("coin_reserv") && coinInfo && coinInfo.active && (
        <section className="py-8 px-4 relative" data-testid="coin-reserve-section" style={{ order: sectionOrder("coin_reserv", 8) }}>
          <SectionControls sectionKey="coin_reserv" />
          <div className="max-w-3xl mx-auto">
            <Link to="/monter" className="block relative overflow-hidden rounded-2xl group"
              style={{ background: "linear-gradient(135deg, #0d1117 0%, #1a0f00 50%, #0d1117 100%)", border: "1px solid rgba(212,175,55,0.3)", boxShadow: "0 0 30px rgba(212,175,55,0.08)" }}
              data-testid="coin-reserve-banner">
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute w-1.5 h-1.5 bg-amber-400/30 rounded-full animate-ping" style={{ top: "20%", right: "15%", animationDuration: "3s" }} />
              </div>
              <div className="relative flex items-center gap-5 p-5 sm:p-6">
                <div className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 relative">
                  <div className="absolute inset-0 bg-amber-500/10 rounded-full blur-xl"></div>
                  <img src={coinInfo.coin_image} alt={coinInfo.coin_name} className="w-full h-full object-contain relative z-10 rounded-full group-hover:scale-105 transition-transform" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-amber-400/70 text-xs uppercase tracking-wider font-['Oswald'] mb-0.5">Husk at reservere din mønt</p>
                  <h3 className="text-xl sm:text-2xl font-['Oswald'] font-bold text-white tracking-wide mb-1">
                    {coinInfo.coin_name} <span className="text-amber-400/60 text-base">blev revet væk!</span>
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-['Oswald'] font-black text-amber-400">{coinInfo.coin_price} kr</span>
                    {coinInfo.orders_count > 0 && (
                      <span className="text-emerald-400/70 text-xs">{coinInfo.orders_count} reserveret</span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 hidden sm:block">
                  <span className="px-4 py-2 rounded-xl bg-amber-500 text-black font-['Oswald'] font-bold text-sm uppercase tracking-wider group-hover:bg-amber-400 transition-all">
                    Reserver nu
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* ===== GALLERI SECTION - Simple Carousel ===== */}
      {sectionVisible("galleri") && (
      <section className="py-12 px-4 relative overflow-hidden" data-testid="gallery-section" style={{ order: sectionOrder("galleri", 9) }}>
        <SectionControls sectionKey="galleri" />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white tracking-wide mb-2 neon-text">
              GALLERI
            </h2>
            <div className="industrial-divider w-16 mx-auto"></div>
          </div>

          {loadingGallery ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border border-[#00d4ff]/50 border-t-[#00d4ff] rounded-full animate-spin"></div>
            </div>
          ) : galleryAlbums.length > 0 ? (
            <GalleryCarousel3D albums={galleryAlbums} />
          ) : (
            <div className="text-center py-16">
              <Image size={32} className="mx-auto text-gray-700 mb-4" />
              <p className="text-gray-500 text-sm">Ingen albums endnu</p>
            </div>
          )}

          <div className="text-center mt-6">
            <Link to="/galleri" className="radial-btn px-6 py-3 inline-flex items-center gap-2 text-sm">
              Se Hele Galleriet <Image size={16} />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* ===== BOOK OS SECTION ===== */}
      {sectionVisible("book") && (
      <section className="py-12 px-4 relative" data-testid="booking-section" style={{ order: sectionOrder("book", 10) }}>
        <SectionControls sectionKey="book" />
        <div className="max-w-3xl mx-auto relative z-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white tracking-wide mb-2 neon-text">
            BOOK THE OTHERS
          </h2>
          <div className="industrial-divider w-16 mx-auto mb-4"></div>
          <p className="text-gray-500 leading-relaxed mb-4 max-w-lg mx-auto text-sm">
            Planlægger du et event? Book The Others og få en autentisk MC-oplevelse.
          </p>
          
          <div className="max-w-xl mx-auto mb-6">
            <div className="mc-card aspect-[16/9] overflow-hidden"
                 style={{ boxShadow: '0 0 30px rgba(0,0,0,0.8)' }}>
              <EditableImage
                pageKey="homepage"
                slot="book-event"
                defaultSrc="https://customer-assets.emergentagent.com/job_f2f6ea6c-dbe8-433e-9b93-cb19aefe9833/artifacts/0dsydzab_539D2794-A9AF-46F6-80D3-474DE7760B19.png"
                overrideSrc={imgOverride("book-event")}
                onSaved={reloadImageOverrides}
                alt="The Others MC at event"
                className="w-full h-full"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 mb-6 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full" style={{ boxShadow: '0 0 6px rgba(0,212,255,0.6)' }}></div>
              Motorcykeludstillinger
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full" style={{ boxShadow: '0 0 6px rgba(0,212,255,0.6)' }}></div>
              Live musik events
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full" style={{ boxShadow: '0 0 6px rgba(0,212,255,0.6)' }}></div>
              Velgørenhed
            </span>
          </div>
          
          <Link to="/book" className="chrome-btn px-6 py-3 inline-flex items-center gap-2 text-sm">
            Book Os Nu <CalendarIcon size={16} />
          </Link>
        </div>
      </section>
      )}

      {/* ===== ANDRE KLUBBER SECTION ===== */}
      {sectionVisible("andre") && (
      <section className="py-12 px-4 relative" data-testid="andre-klubber-section" style={{ order: sectionOrder("andre", 11) }}>
        <SectionControls sectionKey="andre" />
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white tracking-wide mb-2 neon-text">
              ANDRE KLUBBER
            </h2>
            <div className="industrial-divider w-16 mx-auto"></div>
          </div>

          <div 
            className="mc-card max-w-md mx-auto overflow-hidden transition-all duration-300 hover:-translate-y-1"
          >
            <div className="p-8 text-center">
              <div className="mb-6">
                <EditableImage
                  pageKey="homepage"
                  slot="baremc-logo"
                  defaultSrc={baremcLogo}
                  overrideSrc={imgOverride("baremc-logo")}
                  onSaved={reloadImageOverrides}
                  alt="BareMC Logo"
                  className="h-32 mx-auto"
                />
              </div>
              
              <h3 className="text-lg font-['Oswald'] font-bold text-white mb-2">
                BareMC
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                BareMC er en samlet oversigt over motorcykelklubber og arrangementer i Danmark.
              </p>
              
              <a 
                href="https://baremc.dk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="radial-btn px-6 py-2.5 inline-flex items-center gap-2 text-sm"
              >
                Besøg BareMC
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ===== KONTAKT SECTION ===== */}
      {sectionVisible("kontakt") && (
      <section className="py-12 px-4 relative" data-testid="contact-section" style={{ order: sectionOrder("kontakt", 12) }}>
        <SectionControls sectionKey="kontakt" />
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white tracking-wide mb-2 neon-text">
            KONTAKT OS
          </h2>
          <div className="industrial-divider w-16 mx-auto mb-6"></div>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6 text-sm">
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <MapPin size={14} className="text-[#00d4ff]" />
              <span>Marienbergvej 92, 4760 Vordingborg</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <Mail size={14} className="text-[#00d4ff]" />
              <span>kontakt@theothers.dk</span>
            </div>
          </div>
          
          <Link to="/kontakt-os" className="chrome-btn px-6 py-3 inline-flex items-center gap-2 text-sm">
            Kontakt Os <Mail size={16} />
          </Link>
        </div>
      </section>
      )}

      {/* ===== FOOTER ===== */}
      <footer className="py-12 px-4 border-t border-[#00d4ff]/10">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-center mb-8">
            <button 
              onClick={handleShare}
              className="chrome-btn group flex items-center justify-center gap-3 py-3 px-6 transition-all duration-300 hover:scale-[1.02]"
              data-testid="share-section-btn"
            >
              <Share2 size={16} className="text-[#00d4ff] group-hover:text-[#00f3ff] transition-colors" />
              <div className="text-left">
                <p className="text-[#00d4ff] font-['Oswald'] tracking-wider text-sm uppercase">Del The Others</p>
              </div>
            </button>
          </div>

          <div className="flex justify-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_riders-collective/artifacts/qos9b6ai_291A9EF4-2BE4-4271-BA5B-1C9215F4569F.png"
              alt="The Others MC"
              className="w-28 h-28 object-contain"
              style={{ filter: 'drop-shadow(0 0 15px rgba(0, 212, 255, 0.3))' }}
            />
          </div>

          <div className="flex justify-center gap-3 mb-8">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 flex items-center justify-center transition-all duration-300 hover:scale-110"
                style={{
                  background: social.color,
                  border: 'none',
                  boxShadow: `0 4px 15px ${social.color}40`,
                  borderRadius: '12px'
                }}
                title={social.name}
                data-testid={`social-${social.icon}`}
              >
                {social.icon === 'fb' && (
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                )}
                {social.icon === 'tiktok' && (
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                )}
                {social.icon === 'yt' && (
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                )}
                {social.icon === 'spotify' && (
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                )}
                {social.icon === 'instagram' && (
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                )}
              </a>
            ))}
          </div>

          <div className="text-center">
            <p className="text-gray-600 text-xs mb-4">Bliv en del af fællesskabet</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/medlemskab" className="radial-btn px-6 py-2.5 text-sm">
                Ansøg Medlemskab
              </Link>
              <Link to="/login" className="ghost-blue-btn px-6 py-2.5 text-sm">
                Log Ind
              </Link>
            </div>
          </div>

          <p className="text-center text-gray-700 text-xs mt-10">
            © {new Date().getFullYear()} The Others MC Danmark
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
